import { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useGroupKey, useInlineFieldEditingHoverStyles, useJoinKeyPathToString, useRegisterInlineFieldEditor, useWasUndoRedoEventWithData } from '../lib/InlineFieldEditingHooks';
import styled from 'styled-components';
// @ts-ignore not typed
import { useBus } from '../context/Bus';
import { computeUpdatedImageSizePreservingAspectRatio, updateImageDom } from '../lib/domUtils/imageDomUtils';
import ImageSizeTypes from 'ContentUtils/constants/ImageSizeTypes';
import { getCanUseFieldSettingsToolbar } from 'ContentEditorUI/redux/selectors/authSelectors';
import FieldSettingsToolbar from './fieldSettingsToolbar';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function processFilePickerResponse(url, width, height, currentSizeType, mergeUpdatedSubFieldValues) {
  const updatedValueToMerge = {
    src: url
  };
  if (!currentSizeType || currentSizeType === ImageSizeTypes.AUTO) {
    mergeUpdatedSubFieldValues(updatedValueToMerge, true);
    return;
  }
  if (width != null && height != null) {
    if (currentSizeType === ImageSizeTypes.AUTO_CUSTOM_MAX) {
      updatedValueToMerge.max_width = width;
      updatedValueToMerge.max_height = height;
    } else {
      updatedValueToMerge.width = width;
      updatedValueToMerge.height = height;
    }
    mergeUpdatedSubFieldValues(updatedValueToMerge, true);
    return;
  }
  const newImage = new Image();
  newImage.src = url;
  newImage.decode().then(() => {
    if (currentSizeType === ImageSizeTypes.AUTO_CUSTOM_MAX) {
      updatedValueToMerge.max_width = newImage.width;
      updatedValueToMerge.max_height = newImage.height;
    } else {
      updatedValueToMerge.width = newImage.width;
      updatedValueToMerge.height = newImage.height;
    }
    mergeUpdatedSubFieldValues(updatedValueToMerge, true);
  }).catch(() => {
    mergeUpdatedSubFieldValues({
      src: url
    }, true);
  });
}
const CORNER_WIDTH = 9;
const DRAGGABLE_RESIZE_CORNER_TO_CURSOR = {
  topleft: 'nwse-resize',
  topright: 'nesw-resize',
  bottomleft: 'nesw-resize',
  bottomright: 'nwse-resize'
};
const toPx = px => `${px}px`;
function offsetPositionToDocument(positionType, positionPx, offsetParentRect, docScrollTop) {
  const bodyToPageOffset = offsetParentRect.top + docScrollTop;

  // Translating the rect by `bodyToPageOffset` in the y-direction means adding to both `top` and `bottom`
  switch (positionType) {
    case 'top':
      {
        return positionPx - offsetParentRect.top + bodyToPageOffset;
      }
    case 'right':
      {
        return positionPx - offsetParentRect.left;
      }
    case 'left':
      {
        return positionPx - offsetParentRect.left;
      }
    case 'bottom':
      {
        return positionPx - offsetParentRect.top + bodyToPageOffset;
      }
    default:
      throw new Error('Unexpected position type');
  }
}
function useCursorTypeForCorner(cornerType) {
  const cursor = DRAGGABLE_RESIZE_CORNER_TO_CURSOR[cornerType];
  if (!cursor) {
    console.error(`No cursor type for ${cornerType}`);
    return 'auto';
  }
  return cursor;
}
const StyledCornerElement = styled.div.withConfig({
  displayName: "ImageFieldEditor__StyledCornerElement"
})(({
  theme,
  $cursor,
  $top,
  $left
}) => {
  return {
    width: CORNER_WIDTH,
    height: CORNER_WIDTH,
    position: 'absolute',
    top: $top,
    left: $left,
    backgroundColor: theme.color['fill-info-default'],
    zIndex: 2147483598,
    cursor: $cursor
  };
});
const ImageFieldEditorCorner = /*#__PURE__*/forwardRef(({
  point,
  cornerType,
  onMouseDown
}, ref) => {
  const [x, y] = point;
  const cursor = useCursorTypeForCorner(cornerType);
  const top = Math.round(y - CORNER_WIDTH / 2);
  const left = Math.round(x - CORNER_WIDTH / 2);
  return /*#__PURE__*/_jsx(StyledCornerElement, {
    ref: ref,
    $cursor: cursor,
    $top: top,
    $left: left,
    onMouseDown: e => {
      e.stopPropagation();
      onMouseDown(e, cornerType);
    }
  });
});
const offsetCornerPositionToCornerWidth = position => Math.round(position - CORNER_WIDTH / 2);
function updateRefPositions(rect, corner, element) {
  let left;
  let top;
  const offsetParent = document.body;
  const offsetParentRect = offsetParent.getBoundingClientRect();
  const docScrollTop = document.documentElement.scrollTop;
  switch (corner) {
    case 'topright':
      {
        left = offsetPositionToDocument('right', offsetCornerPositionToCornerWidth(rect.right), offsetParentRect, docScrollTop);
        top = offsetPositionToDocument('top', offsetCornerPositionToCornerWidth(rect.top), offsetParentRect, docScrollTop);
        break;
      }
    case 'topleft':
      {
        left = offsetPositionToDocument('left', offsetCornerPositionToCornerWidth(rect.left), offsetParentRect, docScrollTop);
        top = offsetPositionToDocument('top', offsetCornerPositionToCornerWidth(rect.top), offsetParentRect, docScrollTop);
        break;
      }
    case 'bottomleft':
      {
        left = offsetPositionToDocument('left', offsetCornerPositionToCornerWidth(rect.left), offsetParentRect, docScrollTop);
        top = offsetPositionToDocument('bottom', offsetCornerPositionToCornerWidth(rect.bottom), offsetParentRect, docScrollTop);
        break;
      }
    case 'bottomright':
      {
        left = offsetPositionToDocument('right', offsetCornerPositionToCornerWidth(rect.right), offsetParentRect, docScrollTop);
        top = offsetPositionToDocument('bottom', offsetCornerPositionToCornerWidth(rect.bottom), offsetParentRect, docScrollTop);
        break;
      }
    default:
      return;
  }
  element.style.top = toPx(top);
  element.style.left = toPx(left);
}
const StyledImageFieldEditorContainer = styled.div.withConfig({
  displayName: "ImageFieldEditor__StyledImageFieldEditorContainer"
})([".hs-private.hs-private.hs-private &{position:absolute;top:0;left:0;z-index:2147483598;}"]);
export default function ImageFieldEditor({
  node,
  schema,
  onUpdate,
  currentValue,
  name,
  keyPath,
  moduleName,
  moduleSchema,
  fieldOverrides,
  onFocusCallback,
  onBlurCallback
}) {
  useInlineFieldEditingHoverStyles(node);
  const wasUndoRedo = useWasUndoRedoEventWithData(currentValue);
  const [isFocused, setIsFocused] = useState(false);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const isInteractingWithToolbarRef = useRef(false);
  const canUseFieldSettingsToolbar = useSelector(getCanUseFieldSettingsToolbar);
  const bus = useBus();
  const {
    resizable
  } = schema;
  useRegisterInlineFieldEditor(moduleName);
  const fieldKeyString = useJoinKeyPathToString(keyPath);
  const groupKey = useGroupKey(moduleName, fieldKeyString, name);

  // Can become problematic from a perf perspective
  const mergeUpdatedSubFieldValues = useCallback((updatedValues, applyChangesToDom = false) => {
    const mergedValue = Object.assign({}, currentValue, updatedValues);
    if (applyChangesToDom) {
      updateImageDom(node, mergedValue);
    }
    onUpdate(keyPath, mergedValue, {
      groupKey
    });
  }, [currentValue, groupKey, keyPath, node, onUpdate]);
  const handleOpenFilePicker = useCallback(() => {
    bus.postMessage({
      action: 'inpageOpenFilePicker'
    });
    setFilePickerOpen(true);
  }, [bus]);
  const handleFilePickerResponse = useCallback((url, width, height) => {
    processFilePickerResponse(url, width, height, currentValue.size_type, mergeUpdatedSubFieldValues);
  }, [mergeUpdatedSubFieldValues, currentValue.size_type]);
  useEffect(() => {
    if (filePickerOpen) {
      const handleImageChosen = ({
        data
      }) => {
        handleFilePickerResponse(data.url, data.width, data.height);
        setFilePickerOpen(false);
      };
      const handleFilePickerClosed = () => {
        setFilePickerOpen(false);
      };
      bus.on('post:filePickerImageChosen', handleImageChosen);
      bus.on('post:inpageFilePickerClosed', handleFilePickerClosed);
      return () => {
        bus.off('post:filePickerImageChosen', handleImageChosen);
        bus.off('post:inpageFilePickerClosed', handleFilePickerClosed);
      };
    }
    return () => {};
  }, [bus, filePickerOpen, handleFilePickerResponse]);

  // Focus management - matching TextFieldEditor pattern
  // When not ungated for the field settings toolbar, skip focus capture
  // so clicks fall through to the module-level handler that opens the sidebar.
  useEffect(() => {
    if (!canUseFieldSettingsToolbar) {
      return;
    }
    const onClick = () => {
      if (document.activeElement !== node) {
        node.focus();
      }
    };
    const onFocus = () => {
      setIsFocused(true);
      onFocusCallback();
    };
    const onBlur = () => {
      if (isInteractingWithToolbarRef.current) {
        return;
      }
      setIsFocused(false);
      onBlurCallback();
    };

    // Make image focusable
    node.setAttribute('tabindex', '0');
    node.addEventListener('click', onClick);
    node.addEventListener('focus', onFocus);
    node.addEventListener('blur', onBlur);
    return () => {
      node.removeEventListener('click', onClick);
      node.removeEventListener('focus', onFocus);
      node.removeEventListener('blur', onBlur);
      node.removeAttribute('tabindex');
    };
  }, [node, moduleName, onFocusCallback, onBlurCallback, canUseFieldSettingsToolbar]);
  const sizeType = currentValue.size_type;
  const [isDragging, setIsDragging] = useState(false);
  const skipNextClickRef = useRef(false);
  const draggingCorner = useRef(null);
  const originalRectRef = useRef(null);
  const dragSource = useRef(null);
  const imageSizeTypeRef = useRef(sizeType);
  imageSizeTypeRef.current = sizeType;
  const topLeftRef = useRef(null);
  const topRightRef = useRef(null);
  const bottomLeftRef = useRef(null);
  const bottomRightRef = useRef(null);
  const lastUpdatedWidthAndHeightRef = useRef({
    width: 0,
    height: 0
  });
  const transientlyUpdateRefPositions = useCallback(clientRect => {
    if (bottomLeftRef.current && bottomRightRef.current && topRightRef.current && topLeftRef.current) {
      updateRefPositions(clientRect, 'topright', topRightRef.current);
      updateRefPositions(clientRect, 'bottomright', bottomRightRef.current);
      updateRefPositions(clientRect, 'bottomleft', bottomLeftRef.current);
      updateRefPositions(clientRect, 'topleft', topLeftRef.current);
    }
  }, []);
  useEffect(() => {
    if (
    // Always apply the `currentValue` if the change came from an undo/redo action
    !isFocused || wasUndoRedo) {
      updateImageDom(node, currentValue);
      if (isFocused) {
        const newBoundingRect = node.getBoundingClientRect();
        transientlyUpdateRefPositions(newBoundingRect);
      }
    }
  }, [currentValue, node, transientlyUpdateRefPositions, wasUndoRedo, isFocused]);
  useEffect(() => {
    const globalClickListener = () => {
      if (skipNextClickRef.current) {
        skipNextClickRef.current = false;
      }
    };
    const globalMouseMoveListener = e => {
      if (isDragging && originalRectRef.current) {
        if (!originalRectRef.current || !draggingCorner.current || !dragSource.current) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const originalRect = originalRectRef.current;
        const x = e.clientX;
        const y = e.clientY;
        const updatedImageDimensions = computeUpdatedImageSizePreservingAspectRatio(originalRect, draggingCorner.current, dragSource.current, x, y);
        if (!updatedImageDimensions) {
          return;
        }
        let {
          newWidth,
          newHeight
        } = updatedImageDimensions;
        newWidth = newWidth > 0 ? newWidth : 1;
        newHeight = newHeight > 0 ? newHeight : 1;
        lastUpdatedWidthAndHeightRef.current.width = newWidth;
        lastUpdatedWidthAndHeightRef.current.height = newHeight;
        // This logic will need to be kept up to date with ImageWidgetTag.java within the cos-renderer
        // Also this is shared in a few places, maybe worth moving it at this point
        if (imageSizeTypeRef.current === ImageSizeTypes.AUTO_CUSTOM_MAX) {
          node.setAttribute('width', '100%');
          node.setAttribute('height', 'auto');
          node.style.removeProperty('width');
          node.style.removeProperty('height');
          node.style.maxWidth = toPx(newWidth);
          node.style.maxHeight = toPx(newHeight);
        } else {
          node.setAttribute('width', toPx(newWidth));
          node.setAttribute('height', toPx(newHeight));
          node.style.removeProperty('width');
          node.style.removeProperty('height');
          node.style.removeProperty('max-width');
          node.style.removeProperty('max-height');
        }
        transientlyUpdateRefPositions(node.getBoundingClientRect());
      }
    };
    const globalMouseUpListener = e => {
      if (isDragging) {
        e.stopPropagation();
        skipNextClickRef.current = true;
        setIsDragging(false);
        if (imageSizeTypeRef.current === ImageSizeTypes.AUTO_CUSTOM_MAX) {
          const {
            width,
            height
          } = lastUpdatedWidthAndHeightRef.current;
          mergeUpdatedSubFieldValues({
            max_width: width,
            max_height: height
          });
        } else {
          mergeUpdatedSubFieldValues(lastUpdatedWidthAndHeightRef.current);
        }
        lastUpdatedWidthAndHeightRef.current.width = 0;
        lastUpdatedWidthAndHeightRef.current.height = 0;
      }
    };
    document.addEventListener('click', globalClickListener);
    document.addEventListener('mousemove', globalMouseMoveListener);
    document.addEventListener('mouseup', globalMouseUpListener);
    return () => {
      document.removeEventListener('click', globalClickListener);
      document.removeEventListener('mousemove', globalMouseMoveListener);
      document.removeEventListener('mouseup', globalMouseUpListener);
    };
  }, [node, isDragging, mergeUpdatedSubFieldValues, transientlyUpdateRefPositions]);
  useEffect(() => {
    const onResizeOrScroll = () => {
      transientlyUpdateRefPositions(node.getBoundingClientRect());
    };
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll);
    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll);
    };
  }, [node, transientlyUpdateRefPositions]);
  const onMouseDown = useCallback((e, selectedCorner) => {
    // Potentially risky because we could maybe end up in a case where we never set this to false
    setIsDragging(true);
    draggingCorner.current = selectedCorner;
    originalRectRef.current = node.getBoundingClientRect();
    dragSource.current = {
      x: e.clientX,
      y: e.clientY
    };
  }, [node]);
  const shouldRenderToolbar = isFocused && moduleSchema && canUseFieldSettingsToolbar;

  // Only render UI when focused (matching TextFieldEditor pattern)
  if (!isFocused) {
    return null;
  }
  const nodeRect = node.getBoundingClientRect();
  const offsetParent = document.body;
  const offsetParentRect = offsetParent.getBoundingClientRect();
  const bodyToPageOffset = offsetParentRect.top + document.documentElement.scrollTop;
  // Translating the rect by `bodyToPageOffset` in the y-direction means adding to both `top` and `bottom`
  const top = nodeRect.top - offsetParentRect.top + bodyToPageOffset;
  const bottom = nodeRect.bottom - offsetParentRect.top + bodyToPageOffset;
  const left = nodeRect.left - offsetParentRect.left;
  const right = nodeRect.right - offsetParentRect.left;
  const points = [[left, top], [right, top], [left, bottom], [right, bottom]];
  const showDraggableCorners = resizable && sizeType !== ImageSizeTypes.AUTO;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(StyledImageFieldEditorContainer, {
      className: "hs-private",
      children: showDraggableCorners && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ImageFieldEditorCorner, {
          cornerType: "topleft",
          point: points[0],
          onMouseDown: onMouseDown,
          ref: topLeftRef
        }), /*#__PURE__*/_jsx(ImageFieldEditorCorner, {
          cornerType: "topright",
          point: points[1],
          onMouseDown: onMouseDown,
          ref: topRightRef
        }), /*#__PURE__*/_jsx(ImageFieldEditorCorner, {
          cornerType: "bottomleft",
          point: points[2],
          onMouseDown: onMouseDown,
          ref: bottomLeftRef
        }), /*#__PURE__*/_jsx(ImageFieldEditorCorner, {
          cornerType: "bottomright",
          point: points[3],
          onMouseDown: onMouseDown,
          ref: bottomRightRef
        })]
      })
    }), shouldRenderToolbar && /*#__PURE__*/_jsx(FieldSettingsToolbar, {
      focusedField: {
        name,
        domNode: node,
        path: keyPath
      },
      moduleSchema: moduleSchema,
      fieldOverrides: fieldOverrides || {},
      onFieldChange: (path, value, undoData, metaData) => {
        if (fieldOverrides) {
          onUpdate(path, value, Object.assign({}, undoData, {
            groupKey
          }), metaData);
        }
      },
      onToolbarInteractionStart: () => {
        isInteractingWithToolbarRef.current = true;
      },
      onToolbarInteractionEnd: () => {
        isInteractingWithToolbarRef.current = false;
      }
      // When interacting with the toolbar, we ignore onBlur to keep the UI open.
      // This leaves the field without browser focus while React state is still
      // focused. If the user then clicks outside, no onBlur fires, so we sync here.
      ,
      onClose: () => {
        setIsFocused(false);
        onBlurCallback();
      },
      toolContext: {
        onReplace: handleOpenFilePicker
      }
    })]
  });
}